/* eslint-disable max-lines-per-function */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import CommonTextField from '../../../Components/TextField/textField';
import FormDialog from '../../../Components/Modal/modal';
import SearchDropdown from '../../../Components/Dropdown/dropdown';
import SearchMultiSelect from '../../../Components/MultiSelectDropdown/multiSelectDropdown';
import SimpleSnackbar from '../../../Components/Snackbar/snackbar';
import { addSkillsData, editSkillsData } from '../../../Api/request/skills';
import UseAxios from '../../../Api/http/http';
import './addEditSkill.scss';
import {
  ADD_EDIT_SKILLS,
  CANCEL_TEXT,
  SAVE,
  SELECT,
  UPDATE,
  ADD_EDIT_SKILL_RESPONSE_MSG,
} from '../../../Constants/strings';

const SkillForm = ({
  modalOpen,
  handleClose,
  verticalOption,
  departmentOption,
  setRows,
  isEditMode,
  skillId,
  initialSkillName,
  initialDepartmentValue,
  initialVerticalValue,
}) => {
  const [skillName, setSkillName] = useState(initialSkillName || '');
  const [selectedDepartment, setSelectedDepartment] = useState(
    initialDepartmentValue || null,
  );
  const [selectedVerticals, setSelectedVerticals] = useState(
    initialVerticalValue || [],
  );
  const [skillError, setSkillError] = useState('');
  const [departmentError, setDepartmentError] = useState('');
  const [verticalError, setVerticalError] = useState('');
  const [snackbar, setSnackbar] = useState({
    message: '',
    severity: '',
    open: false,
  });

  useEffect(() => {
    setInitialValues();
  }, [
    initialSkillName,
    initialDepartmentValue,
    initialVerticalValue,
    departmentOption,
    verticalOption,
  ]);

  // Ensure initialVerticalValue is an array with the correct format
  const getInitialVerticalArray = (verticalValue) => {
    return verticalValue.length > 0 && typeof verticalValue[0] === 'string'
      ? verticalValue[0].split(',').map((value) => value.trim())
      : [];
  };

  const setInitialValues = () => {
    setSkillName(initialSkillName || '');
    setSelectedDepartment(
      initialDepartmentValue
        ? departmentOption.find(
          (option) => option.name === initialDepartmentValue,
        )
        : null,
    );

    const initialVerticalArray = getInitialVerticalArray(initialVerticalValue);
    const filteredVerticals = verticalOption.filter((option) =>
      initialVerticalArray.includes(option.name),
    );

    setSelectedVerticals(filteredVerticals);
  };

  // Resets all form values and errors to their initial state
  const resetValues = () => {
    setSkillName('');
    setSelectedDepartment(null);
    setSelectedVerticals([]);
    setSkillError('');
    setDepartmentError('');
    setVerticalError('');
  };

  const handleCloseWithReset = () => {
    resetValues();
    handleClose();
  };

  // Displays the snackbar with a message and severity
  const snackbarOpen = (message, severity) => {
    setSnackbar({ message, severity, open: true });
    setTimeout(() => setSnackbar((prev) => ({ ...prev, open: false })), 4000);
  };

  // Handles the API response for editing skill
  const handleEditResponse = (status, skillIdValue) => {
    switch (status) {
    case 200:
      updateRowsWithEditedSkill(skillIdValue);
      handleCloseWithReset();
      snackbarOpen(
        `${ADD_EDIT_SKILL_RESPONSE_MSG.SKILL_UPDATED_SUCCESS}`,
        'success',
      );
      break;
    case 400:
      setSkillError(`${ADD_EDIT_SKILL_RESPONSE_MSG.FILL_REQUIRED_FIELD}`);
      break;
    case 404:
      handleCloseWithReset();
      snackbarOpen(
        `${ADD_EDIT_SKILL_RESPONSE_MSG.SKILL_ALREADY_EXISTS}`,
        'warning',
      );
      break;
    default:
      handleCloseWithReset();
      snackbarOpen(
        `${ADD_EDIT_SKILL_RESPONSE_MSG.OOPS_TRY_AGAIN}`,
        'warning',
      );
      break;
    }
  };

  // Handles the API response for adding or editing skill
  const handleResponse = (responseData) => {
    const status
      = responseData.data.status || responseData.data.response.data.statusCode;
    if (isEditMode) {
      handleEditResponse(status, responseData.data.skillId);
    } else {
      handleAddResponse(status);
    }
  };

  const updateRowsWithEditedSkill = (skillIdVal) => {
    setRows((prevRows) => prevRows.map((row) => updateRow(row, skillIdVal)));
  };

  // Updates a single row with the edited skill details
  const updateRow = (row, skillIdValue) => {
    if (row.id === skillIdValue) {
      return {
        ...row,
        skillName: skillName,
        department: selectedDepartment.name,
        verticals: selectedVerticals
          .map((vertical) => vertical.name)
          .join(', '),
      };
    }
    return row;
  };

  // Handles the API response for adding a skill
  const handleAddResponse = (status) => {
    switch (status) {
    case 200:
      snackbarOpen(
        ADD_EDIT_SKILL_RESPONSE_MSG.SKILL_ADDED_SUCCESS,
        'success',
      );
      setRows(); // Call setRows when adding skill successfully
      handleCloseWithReset();
      break;
    case 404:
      handleCloseWithReset();
      snackbarOpen(
        `${ADD_EDIT_SKILL_RESPONSE_MSG.SKILL_ALREADY_EXISTS}`,
        'warning',
      );
      break;
    default:
      snackbarOpen(ADD_EDIT_SKILL_RESPONSE_MSG.OOPS_TRY_AGAIN, 'warning');
      handleCloseWithReset();
      break;
    }
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    setSkillName(value);
    validateSkillName(value);
  };

  // Validation for skillNameValue
  const validateSkillName = (value) => {
    if (value.length < 3 || value.length > 20) {
      setSkillError(`${ADD_EDIT_SKILL_RESPONSE_MSG.SKILL_NAME_MUST_BE}`);
    } else if (/ {2,}/.test(value)) {
      setSkillError(`${ADD_EDIT_SKILL_RESPONSE_MSG.SKILL_NAME_INVALID}`);
    } else {
      setSkillError('');
    }
  };

  const handleSaveOrUpdate = async () => {
    if (skillError !== '' || departmentError !== '' || verticalError !== '') {
      return;
    }
    const payload = {
      skillId: skillId,
      skillName,
      departmentId: selectedDepartment?.id,
      verticalId: selectedVerticals.map((vertical) => vertical.id),
    };

    if (
      !payload.skillName
      || !payload.departmentId
      || payload.verticalId.length === 0
    ) {
      setSkillError(
        !payload.skillName
          ? ADD_EDIT_SKILL_RESPONSE_MSG.FILL_REQUIRED_FIELD
          : '',
      );
      setDepartmentError(
        !payload.departmentId
          ? ADD_EDIT_SKILL_RESPONSE_MSG.FILL_REQUIRED_FIELD
          : '',
      );
      setVerticalError(
        payload.verticalId.length === 0
          ? ADD_EDIT_SKILL_RESPONSE_MSG.FILL_REQUIRED_FIELD
          : '',
      );
      return;
    }

    try {
      const response = await UseAxios(
        isEditMode
          ? editSkillsData(
            payload.skillId,
            payload.skillName,
            payload.departmentId,
            payload.verticalId,
          )
          : addSkillsData(
            payload.skillName,
            payload.departmentId,
            payload.verticalId,
          ),
      );
      handleResponse(response);
    } catch (error) {
      snackbarOpen(ADD_EDIT_SKILL_RESPONSE_MSG.OOPS_TRY_AGAIN, 'warning');
      console.error('Error adding or updating skill', error);
    }
  };

  // main ui component
  return (
    <div>
      <FormDialog
        title={
          isEditMode ? ADD_EDIT_SKILLS.EDIT_SKILL : ADD_EDIT_SKILLS.ADD_SKILL
        }
        open={modalOpen}
        button1Action={handleCloseWithReset}
        button2Action={handleSaveOrUpdate}
        dialogPaperCss="skill-dialog-paper"
        dialogCss="skill-dialog"
        dialogBodycontent={
          <div className="skill-dialog-body">
            <CommonTextField
              label={ADD_EDIT_SKILLS.SKILL_NAME}
              value={skillName}
              onChange={handleInputChange}
              placeholder={ADD_EDIT_SKILLS.ENTER_SKILL_NAME}
              autoFocus
              size="small"
              id="skill-name-text"
            />
            {skillError && (
              <div className="skill-error">
                <small>{skillError}</small>
              </div>
            )}
            <div className="skill-dropdown">
              <SearchDropdown
                options={departmentOption}
                label={ADD_EDIT_SKILLS.DEPARTMENT}
                placeholder={SELECT}
                value={selectedDepartment}
                onChange={(event) => {
                  setSelectedDepartment(event.target.value);
                  setDepartmentError('');
                }}
                id="skill-department-dropdown"
              />
              {departmentError && (
                <div className="skill-error">
                  <small>{departmentError}</small>
                </div>
              )}
            </div>
            <div className="skill-multi-select">
              <SearchMultiSelect
                options={verticalOption}
                label={ADD_EDIT_SKILLS.VERTICAL}
                placeholder={SELECT}
                value={selectedVerticals}
                onChange={(event) => {
                  setSelectedVerticals(event.target.value);
                  setVerticalError('');
                }}
                id="skill-vertical-dropdown"
              />
              {verticalError && (
                <div className="skill-error">
                  <small>{verticalError}</small>
                </div>
              )}
            </div>
          </div>
        }
        button1={CANCEL_TEXT}
        button2={isEditMode ? UPDATE : SAVE}
        cancelButtonId="skill-cancel-btn"
        submitButtonId="skill-submit-btn"
      />
      <SimpleSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        alertCss={`skill-snackbar ${snackbar.severity}`}
      />
    </div>
  );
};

SkillForm.propTypes = {
  modalOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  verticalOption: PropTypes.array.isRequired,
  departmentOption: PropTypes.array.isRequired,
  setRows: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool,
  skillId: PropTypes.number,
  initialSkillName: PropTypes.string,
  initialDepartmentValue: PropTypes.string,
  initialVerticalValue: PropTypes.array,
};

SkillForm.defaultProps = {
  isEditMode: false,
  skillId: null,
  initialSkillName: '',
  initialDepartmentValue: '',
  initialVerticalValue: [],
};

export default SkillForm;
